import { AxiosError } from 'axios'
import uniq from 'lodash/uniq'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { task } from 'mobx-task'
import { distinctUntilChanged, Subscription } from 'rxjs'
import { func, pattern, required, validationContext } from 'validx'
import { AlertFactory, AlertState } from 'alerts/Alert'
import Role from 'iam/models/Role'
import { SearchableUser } from 'iam/models/SearchableUser'
import Skill from 'iam/models/Skill'
import Team from 'iam/models/Team'
import TeamRole from 'iam/models/TeamRole'
import Workspace from 'iam/models/Workspace'
import RoleStore from 'iam/RoleStore'
import SkillStore from 'iam/SkillStore'
import TeamStore from 'iam/TeamStore'
import TeamRoleStore from 'iam/TeamRoleStore'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import ModalViewModel from 'infra/ModalViewModel'
import { Id, Nullable } from 'types/definitions'
import { tapAction, toStream } from 'utils/mobx-util'
import { pluralize } from 'utils/pluralization-util'
import MemberStore from '../../../iam/MemberStore'
import { Portal as DtoPortal, UserSource } from '../../../iam/dtos/member'
import { match } from 'utils/pattern-match-util'
import { SearchableUserDTO } from 'iam/dtos/searchable-user'
import { Collection } from 'infra/collection'
import { CustomerStore } from '../../../iam/CustomerStore'

interface InviteResults {
  email: string
  success?: boolean
  error?: AxiosError
}

/**
 * List of portals the workspace invitation can link to.
 */
export enum Portal {
  CustomerPortal = 'Customer Portal',
  WorkPortal = 'Work Portal',
  Txf = 'TXF Portal',
}

/**
 * Sign-in type selection.
 */
export type SignInType = 'UsernamePassword' | 'AccessLink'

export class WorkspaceInvitationViewModel extends ModalViewModel {
  /**
   * Subscription to stream of changes to observables.
   */
  private subscription = new Subscription()

  /**
   * The list of workspace roles.
   */
  @observable
  workspaceRoles: Role[] = []

  /**
   * The list of available skills.
   */
  @observable
  availableSkills: Skill[] = []

  /**
   * The list of teams.
   */
  @observable
  teams: Team[] = []

  /**
   * The list of team roles.
   */
  @observable
  teamRoles: TeamRole[] = []

  /**
   * The list of portals.
   */
  @observable
  readonly portals = Object.values(Portal)

  /**
   * The current content of the email address text area.
   */
  @observable
  emailAddressTextArea = ''

  /**
   * The currently selected workspace.
   */
  @observable
  workspace: Nullable<Workspace> = null

  /**
   * The currently selected workspace role.
   */
  @observable
  workspaceRole: Nullable<Role> = null

  /**
   * The currently selected skills.
   */
  @observable
  skills: Skill[] = []

  /**
   * The currently selected team.
   */
  @observable
  team: Nullable<Team> = null

  /**
   * The currently selected team role.
   */
  @observable
  teamRole: Nullable<TeamRole> = null

  /**
   * The currently selected portal.
   */
  @observable
  portal: Portal = Portal.CustomerPortal

  /**
   * The currently selected sign-in type.
   */
  @observable
  signInType: SignInType = 'UsernamePassword'

  /**
   * Customer given name (first name).
   */
  @observable
  customerGivenName = ''

  /**
   * Customer family name (last name).
   */
  @observable
  customerFamilyName = ''

  /**
   * Customer phone number.
   */
  @observable
  customerPhoneNumber = ''

  /**
   * Customer email address.
   */
  @observable
  customerEmail = ''

  /**
   * Validation rules for regular workspace invitations.
   */
  invitationValidation = validationContext(this, {
    emailAddressTextArea: [
      func(
        () => this.emailAddresses.length > 0,
        'You must enter at least one valid email address.',
      ),
    ],
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
    workspaceRole: [
      func(
        () => this.workspaceRole !== null,
        'You must select a workspace role.',
      ),
    ],
    teamRole: [
      func(
        () => this.team === null || this.teamRole !== null,
        'If you select a team, you must also select a team role.',
      ),
    ],
  })

  /**
   * Validation rules for customer invitations.
   */
  customerValidation = validationContext(this, {
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
    customerEmail: [
      required(),
      pattern('email'),
      func(({ value }) => {
        return value.length <= 320 || "Email can't be more than 320 characters"
      }),
      func(
        () => this.customerEmail.trim().length > 0,
        'You must enter a valid email address',
      ),
    ],
    customerGivenName: [
      func(
        () => this.customerGivenName.trim().length > 0,
        'You must enter a first name',
      ),
    ],
    customerFamilyName: [
      func(
        () => this.customerFamilyName.trim().length > 0,
        'You must enter a last name',
      ),
    ],
    customerPhoneNumber: [
      func(
        (ctx) =>
          // Only run the phone number rule if we have a value,
          // as phone numbers are optional.
          !ctx.value ||
          pattern(
            // Super basic regex for phone number chars.
            /^[+]?[ 0-9()-]+$/i,
            'This does not look like a phone number.',
          )(ctx),
      ),
      func(({ value }) => {
        return value.length <= 20 || "Phone can't be more than 20 characters"
      }),
    ],
  })

  /**
   * Stream of changes to the invitation form inputs.
   */
  private invitationSelections$ = toStream(() => this.workspace, false).pipe(
    distinctUntilChanged(),
    tapAction((workspace) => {
      if (workspace) {
        this.clearSelections()
        this.updateOptions()
      }
    }),
  )

  constructor(
    private _selectedWorkspace: () => Workspace,
    private _selectedUsers: () => Set<Id>,
    private _loadedUsers: () => SearchableUser[],
    private _storedUsers: () => Collection<SearchableUserDTO, SearchableUser>,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private roleStore: RoleStore,
    private skillStore: SkillStore,
    private teamStore: TeamStore,
    private teamRoleStore: TeamRoleStore,
    private memberStore: MemberStore,
    private customerStore: CustomerStore,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Activate the view model.
   */
  activate() {
    this.subscription = this.invitationSelections$.subscribe()
  }

  /**
   * Deactivate the view model.
   */
  deactivate() {
    this.subscription.unsubscribe()
  }

  /**
   * The currently selected workspace.
   */
  @computed
  get selectedWorkspace(): Nullable<Workspace> {
    return this._selectedWorkspace() === Workspace.none
      ? null
      : this._selectedWorkspace()
  }

  /**
   * The currently selected users.
   */
  @computed
  get selectedUsers() {
    return this._selectedUsers()
  }

  /**
   * The list of all users.
   */
  @computed
  get users() {
    return this._loadedUsers()
  }

  /**
   * The list of all users.
   */
  @computed
  get storedUsers() {
    return this._storedUsers()
  }

  /**
   * The list of workspaces available to the user.
   */
  @computed
  get workspaces() {
    const availableWorkspaces = this.getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.hasPermission('ADMIN_USER_LIST'))
      .map((mb) => mb.workspace)

    return availableWorkspaces
  }

  /**
   * A list of valid email addresses extracted from email address text area.
   */
  @computed
  get emailAddresses() {
    return uniq(
      this.emailAddressTextArea
        .split(/[,|\n]/)
        .map((e) => e.trim().toLowerCase())
        .filter(
          (e) =>
            !!e &&
            validationContext(
              { email: e },
              { email: [pattern('email')] },
            ).validate().isValid,
        ),
    )
  }

  /**
   * Whether the customer invitation option should be shown.
   */
  @computed
  get shouldShowCustomerOption() {
    return (
      this.workspace &&
      this.workspace.config?.external_customers_config?.enabled &&
      this.workspace.config?.external_customers_config
        ?.allow_requesting_magic_links
    )
  }

  /**
   * Whether to use access link sign-in type.
   */
  @computed
  get canUseAccessLink() {
    return this.memberForSelectedWorkspace?.hasPermission('CUSTOMER_CREATE')
  }

  /**
   * Whether to use username/password sign-in type.
   */
  @computed
  get canUseUsernameAndPassword() {
    return this.memberForSelectedWorkspace?.hasAllPermissions(
      'ADMIN_ROLE_LIST',
      'ADMIN_TEAMS_VIEW',
      'ADMIN_USER_CREATE',
    )
  }

  @computed
  private get memberForSelectedWorkspace() {
    if (!this.workspace) {
      return null
    }
    const currentMember = this.getCurrentUserWorkspaceMemberships().find(
      ({ member }) => member.workspace.id === this.workspace?.id,
    )
    return currentMember?.member ?? null
  }

  /**
   * Set the list of workspace roles.
   */
  @action.bound
  setWorkspaceRoles(roles: Role[]) {
    this.workspaceRoles = roles
  }

  /**
   * Set the list of available skills.
   */
  @action.bound
  setAvailableSkills(skills: Skill[]) {
    this.availableSkills = skills
  }

  /**
   * Set the list of teams.
   */
  @action.bound
  setTeams(teams: Team[]) {
    this.teams = teams
  }

  /**
   * Set the list of team roles.
   */
  @action.bound
  setTeamRoles(roles: TeamRole[]) {
    this.teamRoles = roles
  }

  /**
   * Set the email addresses text area.
   */
  @action.bound
  setEmailAddressTextArea(emails: string) {
    this.emailAddressTextArea = emails
  }

  /**
   * Set the workspace field.
   */
  @action.bound
  setWorkspace(workspace: Nullable<Workspace>) {
    this.workspace = workspace
    if (this.canUseAccessLink && !this.canUseUsernameAndPassword) {
      this.setSignInType('AccessLink')
      return
    }

    this.setSignInType('UsernamePassword')
  }

  /**
   * Set the workspace role field.
   */
  @action.bound
  setWorkspaceRole(role: Nullable<Role>) {
    this.workspaceRole = role
  }

  /**
   * Set the skills field.
   */
  @action.bound
  setSkills(skills: Skill[]) {
    this.skills = skills
  }

  /**
   * Set the team field.
   */
  @action.bound
  setTeam(team: Nullable<Team>) {
    this.team = team
  }

  /**
   * Set the team role field.
   */
  @action.bound
  setTeamRole(role: Nullable<TeamRole>) {
    this.teamRole = role
  }

  /**
   * Set the portal field.
   */
  @action.bound
  setPortal(portal: Portal) {
    this.portal = portal
  }

  /**
   * Set the sign-in type.
   */
  @action.bound
  setSignInType(type: SignInType) {
    this.signInType = type
  }

  /**
   * Set the customer given name.
   */
  @action.bound
  setCustomerGivenName(name: string) {
    this.customerGivenName = name
  }

  /**
   * Set the customer family name.
   */
  @action.bound
  setCustomerFamilyName(name: string) {
    this.customerFamilyName = name
  }

  /**
   * Set the customer phone number.
   */
  @action.bound
  setCustomerPhoneNumber(phone: string) {
    this.customerPhoneNumber = phone
  }

  /**
   * Set the customer email.
   */
  @action.bound
  setCustomerEmail(email: string) {
    this.customerEmail = email
  }

  /**
   * Update the input options if a workspace is changed.
   */
  @action.bound
  async updateOptions() {
    if (!this.workspace) {
      return
    }

    await this.roleStore.find({ workspace_id: Id.assert(this.workspace.id) })
    await this.skillStore.find({ workspace_id: Id.assert(this.workspace.id) })
    await this.teamStore.findTeams({
      workspace_id: Id.assert(this.workspace.id),
    })
    await this.teamRoleStore.find({
      workspace_id: Id.assert(this.workspace.id),
    })

    this.setWorkspaceRoles(
      this.roleStore.forWorkspace(Id.assert(this.workspace.id)),
    )
    this.setAvailableSkills(
      this.skillStore.forWorkspace(Id.assert(this.workspace.id)),
    )
    this.setTeams(
      this.teamStore.teams.items.filter(
        // We already established that a workspace is selected.
        (t) => t.workspace.id === this.workspace!.id,
      ),
    )
    this.setTeamRoles(
      this.teamRoleStore.forWorkspace(Id.assert(this.workspace.id)),
    )
  }

  /**
   * Clears selections when the workspace is changed.
   */
  @action.bound
  clearSelections() {
    this.setWorkspaceRole(null)
    this.setSkills([])
    this.setTeam(null)
    this.setTeamRole(null)
  }

  /**
   * Initializes the input options.
   */
  @action.bound
  async initialize() {
    if (this.selectedWorkspace !== null) {
      this.setWorkspace(this.selectedWorkspace)

      this.updateOptions()
    }
  }

  /**
   * Opens the dialog with the contextual workspace.
   */
  @override
  show() {
    this.initialize()
    super.show()
  }

  /**
   * Opens the dialog with the contextual user.
   */
  @action.bound
  showWithUser(user: SearchableUser) {
    this.initialize()
    this.setEmailAddressTextArea(user.email)
    super.show()
  }

  /**
   * Opens the dialog with all the selected users.
   */
  @action.bound
  showWithSelections() {
    this.initialize()
    const selectedUserEmailList = this.storedUsers
      .filter((u) => this.selectedUsers.has(u.publicId))
      .map((u) => u.email)
      .join(', ')
    this.setEmailAddressTextArea(selectedUserEmailList)
    super.show()
  }

  /**
   * Clears workspace invitation dialog state.
   */
  @action.bound
  reset() {
    this.setWorkspaceRoles([])
    this.setAvailableSkills([])
    this.setTeams([])
    this.setTeamRoles([])

    this.setEmailAddressTextArea('')
    this.setWorkspace(null)
    this.setWorkspaceRole(null)
    this.setSkills([])
    this.setTeam(null)
    this.setTeamRole(null)
    this.setPortal(Portal.CustomerPortal)
    this.setSignInType('UsernamePassword')
    this.setCustomerGivenName('')
    this.setCustomerFamilyName('')
    this.setCustomerPhoneNumber('')
    this.setCustomerEmail('')

    this.customerValidation.reset()
    this.invitationValidation.reset()
  }

  /**
   * Send regular workspace invitations to the specified users.
   */
  private async submitInvitations(): Promise<void> {
    if (!this.invitationValidation.reset().validate().isValid) {
      return
    }

    const results: InviteResults[] = await Promise.all(
      this.emailAddresses.map((e) =>
        this.memberStore
          .createManually({
            email: e,
            workspace_id: Id.assert(this.workspace!.id),
            role_id: this.workspaceRole?.id,
            skill_ids: this.skills.map((s) => Id.assert(s.id)),
            team_id: this.team?.id,
            team_role_id: this.teamRole?.id,
            source: UserSource.Local,
            portal: match(this.portal, {
              [Portal.WorkPortal]: () => DtoPortal.WorkPortal,
              [Portal.CustomerPortal]: () => DtoPortal.CustomerPortal,
              [Portal.Txf]: () => DtoPortal.Txf,
            }),
          })
          .then(() => {
            return { email: e, success: true }
          })
          .catch((err) => {
            return { email: e, error: err }
          }),
      ),
    )

    const successful = results.filter((r) => r.success)
    const failed = results.filter((r) => r.error)

    // If all invitations succeeded, close dialog.
    if (failed.length < 1) {
      this.newAlert()
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `Invitations sent to ${successful.length} ${pluralize('user', successful.length)}.`,
        )
        .autoDismiss(2500)
        .show()
      this.close()
      return
    }

    // If any invitations failed, populate email text area with the failed email addresses.
    this.newAlert()
      .withState(AlertState.Error)
      .saying(
        `Failed to send invitations to ${failed.length} ${pluralize('user', failed.length)}.`,
      )
      .autoDismiss(2500)
      .show()
    this.setEmailAddressTextArea(failed.map((f) => f.email).join(', '))
  }

  /**
   * Send customer invitation.
   */
  private async submitCustomer(): Promise<void> {
    if (!this.customerValidation.reset().validate().isValid) {
      return
    }

    try {
      await this.customerStore.create({
        workspace_id: Id.assert(this.workspace!.id),
        email: this.customerEmail,
        given_name: this.customerGivenName,
        family_name: this.customerFamilyName,
        phone_number: this.customerPhoneNumber || undefined,
      })

      this.newAlert()
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `Customer invitation sent to ${this.customerEmail}.`,
        )
        .autoDismiss(2500)
        .show()
      this.close()
    } catch (err) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying(`Failed to send customer invitation to ${this.customerEmail}.`)
        .autoDismiss(2500)
        .show()
    }
  }

  /**
   * Submit the form based on the selected invitation type.
   */
  submit = task.resolved(async () => {
    return match(this.signInType, {
      UsernamePassword: () => this.submitInvitations(),
      AccessLink: () => this.submitCustomer(),
    })
  })

  /**
   * Close the dialog and reset selections to default.
   */
  @override
  close() {
    this.reset()
    super.close()
  }
}
